// Simulation — the lockstep update loop + the SIMU hash group, and GobStateMachineMgr (the
// gsmm binding: smid == gid). game/Simulation.cpp:19-650 + game/ht.h:5342+/6280-6290.
//
// Update-order semantics (hash-load-bearing, from Simulation.cpp:404-600):
//   pause gate -> tCurrent += 8, cUpdates++ -> trigger pump every 6 updates (an EVENT the
//   host runs BETWEEN updates) -> PlayerMgr.Update -> UnitGroupMgr.Update -> BuildMgr.Update
//   -> command loop (PlayerDisconnect via host callback; everything else RouteMessage — each
//   command lands a tracked group) -> Clear + gcMessagesPerUpdate = 0 (so CMPU counts only
//   the gob-update phase) -> ScanDispatch (sim timers; none exist in the shipping game) ->
//   gid-SNAPSHOT loop sending kmidReservedUpdate to kfGobStateMachine gobs that pass
//   Decrement() (always, in MP) -> DispatchDelayedMessages -> local notification seams.

import type { SimContext } from '../context.ts';
import { quad } from '../hash/quads.ts';
import { StateFrame } from '../hash/stateframe.ts';
import { Gob, kfGobStateMachine } from '../gob/gob.ts';
import { CommandQueue } from '../sm/commandqueue.ts';
import {
  Message,
  newMessage,
  ksmidNull,
  kmidReservedUpdate,
  kmidPlayerDisconnect,
} from '../sm/message.ts';
import { StateMachine, StateMachineMgr } from '../sm/statemachine.ts';
import { TimerMgr } from '../sm/timer.ts';
import { kfPlrStructureAttacked } from '../player/side.ts';

// game/ht.h:6280-6290 — smid IS the gid; lookups do NOT require active gobs
export class GobStateMachineMgr extends StateMachineMgr {
  override GetId(psm: StateMachine): number {
    return (psm as Gob).GetId();
  }
  override GetStateMachine(smid: number): StateMachine | null {
    return this.ctx.gobm.GetGob(smid, false);
  }
}

// game/ht.h:1017 — 80ms per update; tCurrent advances kcmsUpdate/10 = 8 ticks
const kcmsUpdate = 80;
// game/Simulation.cpp:421 — trigger pump cadence
const kcupdTriggerMgrUpdate = 6;

/** The collaborators the loop drives; full classes implement these as they land (M3). */
export interface UnitGroupMgrLike {
  Update(): void;
}
export interface LevelLike {
  GetUnitGroupMgr(): UnitGroupMgrLike;
  // The real Level supplies the full TriggerMgr (callers use Enable/SetSwitch/GetSwitch/
  // IsConditionTrue/AddPeriodicTimer/GetCountdownTimer/SetConditionTrue). Typed as the real
  // class so those calls type-check; non-optional matches Level.GetTriggerMgr(). Type-only.
  GetTriggerMgr(): import('../trigger/triggermgr.ts').TriggerMgr;
  /** Level.GetVersion() — m_nVersionLevel reads it during trigger-condition load. */
  GetVersion(): number;
}
export interface BuildMgrLike {
  Update(): void;
  OnBuilt?(punt: unknown, pbldr: unknown): void;
}

export class Simulation extends TimerMgr {
  private ctx: SimContext;

  m_plvl: LevelLike | null = null; // Simulation.cpp:21
  m_pbldm: BuildMgrLike | null = null;
  m_fPaused = false;
  m_tCurrent = 0;
  // Simulation.cpp:202 (per-level init): the FIRST update is update 0 — and the lockstep
  // bootstrap depends on it: updates(-1)+1 == cUpdatesBlock(0) consumes the first
  // UpdateNetMessage without a hash report (the m_cUpdatesBlock != 0 guard).
  m_cUpdates = -1;
  m_cupdTriggerMgrUpdateLast = 0;
  m_fUpdateTriggersPending = false; // mirrors Game::m_fUpdateTriggers (per-game state)
  m_wxView = 0; // Simulation.cpp view position: presentation, but SAVED state
  m_wyView = 0;

  constructor(ctx: SimContext) {
    super();
    this.ctx = ctx;
  }

  // game/Simulation.cpp:386-389 — the deterministic sim clock (TimerMgr override)
  override GetTickCount(): number {
    return this.m_tCurrent;
  }

  GetUpdateCount(): number {
    return this.m_cUpdates;
  }
  GetLevel(): LevelLike | null {
    return this.m_plvl;
  }
  IsPaused(): boolean {
    return this.m_fPaused;
  } // Simulation.cpp:1228-1231

  // game/Simulation.cpp:191-218 — per-level counter reset + the BuildMgr's birth (the
  // caller passes the constructed BuildMgr: TS has no global gcmdq/new-in-place)
  PerLevelInit(pbldm: BuildMgrLike): boolean {
    this.m_cUpdates = -1;
    this.m_tCurrent = 0;
    this.m_pbldm = pbldm;
    this.m_cupdTriggerMgrUpdateLast = 0;
    return true;
  }

  // game/Simulation.cpp:299-383 (knVerSimState 3). The LEVEL constructs itself from
  // the stream in C++ (Level::LoadState re-runs Init by name); the TS caller passes the
  // level vector through `levelDataFor` since there is no pak reader here.
  LoadState(
    pstm: import('../sm/stream.ts').Stream,
    makeLevel: () => { LoadState(pstm: import('../sm/stream.ts').Stream): boolean },
  ): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 3) return false;
    this.m_cUpdates = pstm.ReadDword() | 0;
    const wxView = (pstm.ReadWord() << 16) >> 16;
    const wyView = (pstm.ReadWord() << 16) >> 16;
    this.m_tCurrent = pstm.ReadDword() | 0;
    this.m_cupdTriggerMgrUpdateLast = pstm.ReadDword() | 0;

    // Level::LoadState assigns itself to gsim's level pointer (m_plvl) as in C++
    const plvl = makeLevel();
    if (!plvl.LoadState(pstm)) return false;

    (this.ctx.smm as unknown as { LoadState(p: unknown): boolean }).LoadState(pstm);
    this.ctx.cmdq.LoadState(pstm);

    this.SetViewPos(wxView, wyView);
    return pstm.IsSuccess();
  }

  // game/Simulation.cpp:340-369
  SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(3);
    pstm.WriteDword(this.m_cUpdates);
    pstm.WriteWord(this.m_wxView);
    pstm.WriteWord(this.m_wyView);
    pstm.WriteDword(this.m_tCurrent);
    pstm.WriteDword(this.m_cupdTriggerMgrUpdateLast);
    (this.m_plvl as unknown as { SaveState(p: unknown): boolean }).SaveState(pstm);
    (this.ctx.smm as unknown as { SaveState(p: unknown): boolean }).SaveState(pstm);
    this.ctx.cmdq.SaveState(pstm);
    return pstm.IsSuccess();
  }

  // Simulation.cpp SetViewPos — view scroll is presentation; the coords are sim-saved
  SetViewPos(wxView: number, wyView: number): void {
    this.m_wxView = wxView;
    this.m_wyView = wyView;
  }

  // game.cpp:2246-2258 Game::UpdateTriggers — the driver runs this between updates
  RunPendingTriggerUpdate(): void {
    if (this.m_fUpdateTriggersPending) {
      this.m_fUpdateTriggersPending = false;
      this.m_plvl?.GetTriggerMgr?.()?.Update();
    }
  }

  GetBuildMgr(): BuildMgrLike | null {
    return this.m_pbldm;
  } // Simulation.cpp GetBuildMgr
  Pause(fPause: boolean): void {
    this.m_fPaused = fPause;
  }

  // game/Simulation.cpp:404-600
  Update(pcmdq: CommandQueue): void {
    if (this.m_fPaused) return;

    // HostSoundServiceProc() — host no-op

    this.m_tCurrent += kcmsUpdate / 10;
    this.m_cUpdates++;

    // trigger pump: an event that runs BETWEEN updates (modal actions allowed) —
    // game.cpp:2237 ScheduleUpdateTriggers sets the flag; the event loop's
    // Game::UpdateTriggers (FilterEvent / RunUpdatesNow) fires it before the next update
    if (
      this.m_cupdTriggerMgrUpdateLast === 0 ||
      Math.abs(this.m_cUpdates - this.m_cupdTriggerMgrUpdateLast) >= kcupdTriggerMgrUpdate
    ) {
      this.m_cupdTriggerMgrUpdateLast = this.m_cUpdates;
      this.m_fUpdateTriggersPending = true;
      this.ctx.host.ScheduleUpdateTriggers();
    }

    this.ctx.plrm.Update(this.m_cUpdates);
    this.m_plvl?.GetUnitGroupMgr().Update();
    this.m_pbldm?.Update();

    // process queued commands in wire order
    const amsg = pcmdq.GetFirst();
    const cmsg = pcmdq.GetCount();
    for (let i = 0; i < cmsg; i++) {
      const pmsg = amsg[i];
      if (pmsg.mid === kmidPlayerDisconnect) {
        this.HandlePlayerDisconnect(pmsg);
        continue;
      }
      this.ctx.smm!.RouteMessage(pmsg);
    }
    pcmdq.Clear();

    this.ctx.cMessagesPerUpdate = 0; // CMPU counts only what follows

    this.ScanDispatch(this.m_tCurrent);

    // snapshot gids (insertions/deletions during updates must not affect the walk)
    const msgUpdate = newMessage(kmidReservedUpdate, ksmidNull, 0);
    const agid: number[] = [];
    const gobm = this.ctx.gobm;
    for (let pgobT = gobm.GetFirstGob(); pgobT !== null; pgobT = gobm.GetNextGob(pgobT)) {
      if (pgobT.GetFlags() & kfGobStateMachine) agid.push(pgobT.GetId());
    }

    for (let i = 0; i < agid.length; i++) {
      const pgobT = gobm.GetGob(agid[i], false);
      if (pgobT === null) continue; // destroyed by an earlier gob's update

      if (pgobT.GetUpdateInterval().Decrement(this.ctx.fMultiplayer)) {
        msgUpdate.smidReceiver = agid[i];
        this.ctx.smm!.SendMsg(msgUpdate);
      }
    }

    // delayed AFTER updates: new gobs must not get an Update before their first paint
    this.ctx.smm!.DispatchDelayedMessages();

    // ---- machine-local notification seams (presentation; throttled on the HOST clock) --
    const plrLocal = this.ctx.plrLocal;
    if (plrLocal !== null) {
      if (plrLocal.GetFlags() & kfPlrStructureAttacked) {
        plrLocal.SetFlags(plrLocal.GetFlags() & ~kfPlrStructureAttacked);
        this.ctx.host.NotifyBaseUnderAttack();
      }
      if (
        plrLocal.GetPowerDemand() !== 0 &&
        plrLocal.GetPowerDemand() > plrLocal.GetPowerSupply()
      ) {
        this.ctx.host.NotifyPowerLow();
      }
    }
  }

  // game/Simulation.cpp:608-625 — the disconnect is re-routed as a NetMessage to the game
  // callback (resign/observer flows). That flow is the host/driver's (M3 wires the re-sim
  // equivalents); the sim-side contract is just the dispatch.
  private HandlePlayerDisconnect(pmsg: Message): void {
    this.ctx.host.OnPlayerDisconnect(pmsg.PlayerDisconnect.pid, pmsg.PlayerDisconnect.nReason);
  }

  // game/Simulation.cpp:628-650 — the SIMU group + every state-machine gob + players
  TrackState(frame: StateFrame): void {
    const i = frame.AddCountedValue(quad('SIMU'));
    frame.AddValue(quad('SEED'), this.ctx.rng.GetRandomSeed(), i);
    frame.AddValue(quad('CMPU'), this.ctx.cMessagesPerUpdate, i);
    frame.AddValue(quad('CUPD'), this.m_cUpdates, i);

    const gobm = this.ctx.gobm;
    for (let pgobT = gobm.GetFirstGob(); pgobT !== null; pgobT = gobm.GetNextGob(pgobT)) {
      if (!(pgobT.GetFlags() & kfGobStateMachine)) continue;
      pgobT.TrackState(frame);
    }

    this.ctx.plrm.TrackState(frame);
  }
}
